import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Badge,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FiDownload, FiAward, FiCalendar, FiClock } from 'react-icons/fi';
import { canvasAPI } from '../../services/api';

const StudentCertificateView = () => {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('current'); // 'current' or 'all'
  const [allCertificates, setAllCertificates] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        setLoading(true);
        const data = await canvasAPI.getMyCertificate();
        setCertificateData(data);
      } catch (error) {
        console.error('Error fetching certificate:', error);
        // No mostrar error si es 404 (no tiene certificado aún)
        if (error.response && error.response.status !== 404) {
          toast({
            title: 'Error',
            description: 'No se pudo cargar la información del certificado',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [toast]);

  const loadAllCertificates = async () => {
    try {
      setLoadingAll(true);
      const response = await canvasAPI.getAllMyCertificates();
      setAllCertificates(response.certificates || []);
      setViewMode('all');
    } catch (error) {
      console.error('Error loading all certificates:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial de certificados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingAll(false);
    }
  };

  const handleDownloadById = async (certId, certName) => {
    try {
      await canvasAPI.downloadStudentCertificateById(certId, certName);
      toast({
        title: 'Éxito',
        description: 'Certificado descargado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await canvasAPI.downloadMyCertificate();
      toast({
        title: 'Éxito',
        description: 'Certificado descargado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando información del certificado...</Text>
      </Box>
    );
  }

  if (viewMode === 'all') {
    return (
      <Box maxW="4xl" mx="auto" py={8} px={4}>
        <Button
          onClick={() => setViewMode('current')}
          mb={6}
          variant="ghost"
        >
          &larr; Volver al curso actual
        </Button>

        <Heading size="lg" mb={6}>Mis Certificados</Heading>

        {loadingAll ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="blue.500" />
          </Box>
        ) : (
          <VStack spacing={4} align="stretch">
            {allCertificates.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                No tienes certificados emitidos en ningún curso.
              </Alert>
            ) : (
              allCertificates.map((cert) => (
                <Card key={cert.id} variant="outline">
                  <CardBody>
                    <HStack justify="space-between" wrap="wrap" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" fontSize="lg">{cert.course_name}</Text>
                        <Text fontSize="sm" color="gray.600">{cert.context_title}</Text>
                        <Badge colorScheme="green" mt={1}>Emitido: {new Date(cert.issue_date).toLocaleDateString()}</Badge>
                      </Box>
                      <Button
                        leftIcon={<FiDownload />}
                        colorScheme="blue"
                        variant="outline"
                        isDisabled={!cert.can_download}
                        onClick={() => handleDownloadById(cert.id, `Certificado_${cert.course_name}`)}
                      >
                        Descargar
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>
              ))
            )}
          </VStack>
        )}
      </Box>
    );
  }

  // Vista "Current" (Curso actual)
  const hasCertificate = certificateData && certificateData.has_certificate;

  return (
    <Box maxW="4xl" mx="auto" py={8} px={4}>
      <HStack justify="flex-end" mb={4}>
        <Button
          onClick={loadAllCertificates}
          colorScheme="teal"
          variant="ghost"
          size="sm"
        >
          Ver todos mis certificados
        </Button>
      </HStack>

      {!hasCertificate ? (
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="300px"
          borderRadius="lg"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Sin certificado
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            {certificateData?.message || 'Aún no tienes un certificado emitido para este curso.'}
          </AlertDescription>
          <Text mt={4} fontSize="sm" color="gray.600">
            Consulta con tu instructor sobre los requisitos para obtener el certificado.
          </Text>
        </Alert>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Icon as={FiAward} boxSize={16} color="blue.500" mb={4} />
            <Heading size="xl" mb={2}>
              Tu Certificado
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Felicidades por completar el curso
            </Text>
          </Box>

          {/* Certificate Card */}
          <Card
            variant="outline"
            borderWidth="2px"
            borderColor="blue.500"
            boxShadow="xl"
          >
            <CardBody>
              <VStack spacing={6} align="stretch">
                {/* Certificate ID */}
                <HStack justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    ID del Certificado
                  </Text>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                    {certificateData.certificate.certificate_id}
                  </Badge>
                </HStack>

                <Divider />

                {/* Student Info */}
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Estudiante
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    {certificateData.certificate.student_name}
                  </Text>
                </Box>

                {/* Course Info */}
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Curso
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    {certificateData.certificate.course_name}
                  </Text>
                </Box>

                {/* Duration and Grade */}
                <HStack spacing={8}>
                  <Box flex={1}>
                    <HStack spacing={2} mb={1}>
                      <Icon as={FiClock} color="gray.500" />
                      <Text fontSize="sm" color="gray.500">
                        Duración
                      </Text>
                    </HStack>
                    <Text fontSize="md" fontWeight="medium" color="gray.700">
                      {certificateData.certificate.duration}
                    </Text>
                  </Box>

                  {certificateData.certificate.final_grade && (
                    <Box flex={1}>
                      <Text fontSize="sm" color="gray.500" mb={1}>
                        Calificación Final
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="green.600">
                        {certificateData.certificate.final_grade.toFixed(1)}
                      </Text>
                    </Box>
                  )}
                </HStack>

                <Divider />

                {/* Issue Date */}
                <HStack spacing={2}>
                  <Icon as={FiCalendar} color="gray.500" />
                  <Text fontSize="sm" color="gray.500">
                    Fecha de emisión:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    {new Date(certificateData.certificate.issue_date).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                </HStack>

                {/* Download Button */}
                {certificateData.certificate.can_download ? (
                  <Button
                    colorScheme="blue"
                    size="lg"
                    leftIcon={<FiDownload />}
                    onClick={handleDownload}
                    isLoading={downloading}
                    loadingText="Descargando..."
                    width="full"
                    mt={4}
                  >
                    Descargar Certificado PDF
                  </Button>
                ) : (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>PDF en proceso</AlertTitle>
                      <AlertDescription fontSize="sm">
                        El PDF de tu certificado está siendo generado. Por favor, vuelve a intentar en unos momentos.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Info Box */}
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Información</AlertTitle>
              <AlertDescription fontSize="xs">
                Este certificado es oficial y puede ser verificado usando el ID mostrado arriba.
                Puedes descargar el PDF tantas veces como necesites.
              </AlertDescription>
            </Box>
          </Alert>
        </VStack>
      )}
    </Box>
  );
};

export default StudentCertificateView;
